import React, { useRef } from 'react';
import { Grid, Smartphone, Car, Building2, Shirt, Home, Gamepad, MoreHorizontal } from 'lucide-react';

interface FilterItem {
    id: string;
    label: string;
    icon?: React.ElementType;
}

interface SwipeableFilterBarProps {
    selectedCategory: string | null;
    onSelectCategory: (id: string | null) => void;
    onOpenFilterModal: () => void;
}

const SwipeableFilterBar: React.FC<SwipeableFilterBarProps> = ({
    selectedCategory,
    onSelectCategory,
    onOpenFilterModal
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);

    const categories: FilterItem[] = [
        { id: 'all', label: 'Tümü', icon: Grid },
        { id: 'electronics', label: 'Elektronik', icon: Smartphone },
        { id: 'vehicles', label: 'Vasıta', icon: Car },
        { id: 'real_estate', label: 'Emlak', icon: Building2 },
        { id: 'fashion', label: 'Moda', icon: Shirt },
        { id: 'home', label: 'Ev & Yaşam', icon: Home },
        { id: 'hobby', label: 'Hobi', icon: Gamepad },
    ];

    return (
        <div className="sticky top-[58px] z-[80] bg-white/80 dark:bg-gray-950/80 backdrop-blur-xl border-b border-gray-100/50 dark:border-gray-800/50 py-3 md:hidden transition-all duration-500">
            <div className="flex items-center gap-2.5 px-4 overflow-x-auto scrollbar-hide no-scrollbar snap-x" ref={scrollRef}>

                {/* Categories */}
                {categories.map((cat, index) => {
                    const isSelected = (selectedCategory === cat.id) || (!selectedCategory && cat.id === 'all');
                    const Icon = cat.icon;

                    return (
                        <button
                            key={cat.id}
                            onClick={() => onSelectCategory(cat.id === 'all' ? null : cat.id)}
                            className={`
                                flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-[0.15em] transition-all duration-300 snap-start relative group
                                ${isSelected
                                    ? 'bg-gray-950 dark:bg-white text-white dark:text-gray-950 shadow-[0_8px_30px_rgb(0,0,0,0.12)] dark:shadow-[0_8px_30px_rgb(255,255,255,0.08)] scale-[1.02]'
                                    : 'bg-gray-50/50 dark:bg-gray-900/50 text-gray-400 dark:text-gray-500 border border-gray-100 dark:border-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }
                            `}
                        >
                            {/* Selected Indicator Glow */}
                            {isSelected && (
                                <span className="absolute -inset-[1px] rounded-2xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-sm -z-10 animate-pulse" />
                            )}

                            {Icon && <Icon
                                size={14}
                                strokeWidth={isSelected ? 3 : 2.5}
                                className={`transition-transform duration-500 ${isSelected ? 'scale-110' : 'group-hover:scale-110'}`}
                            />}
                            <span className="relative z-10">{cat.label}</span>

                            {/* Active Dot */}
                            {isSelected && cat.id !== 'all' && (
                                <span className="w-1 h-1 bg-current rounded-full ml-0.5 animate-bounce" />
                            )}
                        </button>
                    );
                })}

                <div className="w-6 flex-shrink-0" />
            </div>
        </div>
    );
};

export default SwipeableFilterBar;
